<template>
  <div ref="rootRef" class="p-advanced-search" :class="{ 'p-advanced-search--home': isHome }">
    <div class="p-advanced-search__input-container" @focusin="onFocus" @focusout="onBlur">
      <p-search-input
        v-model="searchQuery"
        :size="size"
        :placeholder="placeholder"
        :hide-cancel="true"
        @on-debounce="handleSearch"
        @on-clear="clearResults"
        @keyup.enter="navigateToSearchPage"
      />
    </div>
    <div v-if="showResults && searchResults.length > 0" class="p-advanced-search__results">
      <div
        v-for="(result, index) in searchResults"
        :key="index"
        class="p-advanced-search__result-row"
        @click="handleResultClick(result)"
      >
        <div class="p-advanced-search__result-title f-utility-12 t-fg-accent">
          {{ result.title }}
        </div>
        <div class="p-advanced-search__result-desc f-utility-14">
          <span v-html="highlightMatch(result.description)"></span>
        </div>
        <p-divider v-if="index < searchResults.length" class="p-advanced-search__result-divider" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSuggestionList } from '@/api/useSearch'
import { useAppLink } from '@/composables/useAppLink'

interface SearchResult {
  title: string
  description: string
  link: string
}

const props = defineProps({
  placeholder: {
    type: String,
    default: 'Aramak istediğiniz konuyu yazın',
  },
  isHome: {
    type: Boolean,
    default: false,
  },
  enableGlobalShortcut: {
    type: Boolean,
    default: false,
  },
})

const router = useRouter()
const { appLink } = useAppLink()
const searchQuery = ref('')
const searchResults = ref<SearchResult[]>([])
const showResults = ref(false)
const { suggestionList, fetchSuggestionList } = useSuggestionList()
const size = computed(() => (props.isHome ? 'lg' : 'md'))
const language = useI18n().locale.value.split('-')[0]
const rootRef = ref<HTMLElement | null>(null)
const hasFocus = ref(false)
const suppressSuggestions = ref(false)

const navigateToSearchPage = () => {
  if (searchQuery.value && searchQuery.value.length >= 2) {
    const searchPath = '/' + (language === 'tr' ? 'arama' : 'search')
    const baseQuery = { q: searchQuery.value }
    suppressSuggestions.value = true
    clearResults()
    ;(document.activeElement as HTMLElement | null)?.blur()
    hasFocus.value = false
    if (router.currentRoute.value.path === searchPath) {
      router.replace({ path: searchPath, query: baseQuery })
    } else {
      router.push({ path: searchPath, query: baseQuery })
    }
  }
}

const handleSearch = () => {
  if (searchQuery.value.length < 2 || !hasFocus.value || suppressSuggestions.value) {
    searchResults.value = []
    showResults.value = false
    return
  }
  searchResults.value = suggestionList.value.map((suggestion) => {
    let url = suggestion.url.slice(1)
    if (suggestion.type === 'announcement' || suggestion.type === 'dictionary') {
      url = `${suggestion.type}/${url}`
    }
    return {
      title: suggestion.tenant,
      description: suggestion.title,
      link: appLink(url),
    }
  })
  showResults.value = true
}

const clearResults = () => {
  searchResults.value = []
  showResults.value = false
}

const handleResultClick = (result: SearchResult) => {
  window.location.href = result.link
  clearResults()
}

const highlightMatch = (text: string) => {
  if (!searchQuery.value || searchQuery.value.length < 2) {
    return `<span class="t-fg-primary">${text}</span>`
  }
  const searchTerm = escapeRegExp(searchQuery.value)
  const regex = new RegExp(searchTerm, 'gi')
  const parts: { text: string; isMatch: boolean }[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.substring(lastIndex, match.index), isMatch: false })
    }
    parts.push({ text: match[0], isMatch: true })
    lastIndex = regex.lastIndex
  }
  if (lastIndex < text.length) {
    parts.push({ text: text.substring(lastIndex), isMatch: false })
  }
  return parts
    .map((part) => {
      const className = part.isMatch ? 't-fg-accent' : 't-fg-primary'
      return `<span class="${className}">${part.text}</span>`
    })
    .join('')
}

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')
}

watch(searchQuery, async (newValue) => {
  if (newValue.length > 2) {
    await fetchSuggestionList(newValue)
    handleSearch()
  } else {
    clearResults()
  }
})

const onFocus = async () => {
  hasFocus.value = true
  if (searchQuery.value.length >= 2) {
    await fetchSuggestionList(searchQuery.value)
    suppressSuggestions.value = false
    handleSearch()
  }
}

const onBlur = () => {
  hasFocus.value = false
}

const handleGlobalKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    clearResults()
    return
  }
  if (!(props.isHome || props.enableGlobalShortcut)) return
  const isShortcut = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')
  if (!isShortcut) return
  e.preventDefault()
  const input = rootRef.value?.querySelector('input') as HTMLInputElement | null
  input?.focus()
}

const handleOutsideClick = (e: MouseEvent) => {
  const root = rootRef.value
  if (!root) return
  if (root.contains(e.target as Node)) return
  clearResults()
}

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    suppressSuggestions.value = false
  },
)

onMounted(() => {
  window.addEventListener('keydown', handleGlobalKey)
  window.addEventListener('click', handleOutsideClick, { capture: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKey)
  window.removeEventListener('click', handleOutsideClick, { capture: true } as any)
})
</script>
